import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  MessageSquare,
  Send,
  ArrowLeft,
  User,
  Dumbbell
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const messagesEndRef = useRef(null);

  // Check for recipient from URL (e.g., /messages?to=trainer_id)
  const recipientId = searchParams.get('to');
  const recipientName = searchParams.get('name');

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversation/${conversationId}`);
      setMessages(response.data.messages || []);
      setCurrentUserId(response.data.current_user_id);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        fetchMessages(activeConversation.id);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeConversation, fetchMessages]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle direct message to a new recipient
  useEffect(() => {
    if (recipientId && !activeConversation) {
      // Check if conversation already exists
      const existing = conversations.find(c => c.other_participant_id === recipientId);
      if (existing) {
        setActiveConversation(existing);
      } else if (recipientName) {
        // Create temporary conversation state for new chat
        setActiveConversation({
          id: null, // Will be created on first message
          other_participant_id: recipientId,
          other_participant_name: recipientName,
          isNew: true
        });
      }
    }
  }, [recipientId, recipientName, conversations, activeConversation]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const recipientToSend = activeConversation?.other_participant_id;
    if (!recipientToSend) {
      toast.error('No recipient selected');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/messages/send`, {
        recipient_id: recipientToSend,
        content: newMessage.trim()
      });

      setNewMessage('');
      
      // If this was a new conversation, update the conversation ID
      if (activeConversation?.isNew) {
        setActiveConversation(prev => ({
          ...prev,
          id: response.data.conversation_id,
          isNew: false
        }));
      }
      
      // Refresh messages
      if (response.data.conversation_id) {
        await fetchMessages(response.data.conversation_id);
      }
      
      // Refresh conversations list
      await fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="messages-page">
      <Header />
      
      <main className="pt-20 h-screen">
        <div className="max-w-6xl mx-auto h-[calc(100vh-80px)] flex">
          {/* Conversations List - hidden on mobile when viewing a chat */}
          <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-col border-r border-white/10`}>
            <div className="p-4 border-b border-white/10">
              <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Messages
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Chat with your trainers or clients
              </p>
            </div>
            
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Book a session to start messaging</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setActiveConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-white/5 transition-colors ${
                        activeConversation?.id === conv.id ? 'bg-white/5' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          {conv.other_participant_type === 'trainer' ? (
                            <Dumbbell className="w-5 h-5 text-primary" />
                          ) : (
                            <User className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white truncate">
                              {conv.other_participant_name}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {conv.last_message || 'No messages yet'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`${activeConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <button
                    onClick={() => setActiveConversation(null)}
                    className="md:hidden text-gray-400 hover:text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    {activeConversation.other_participant_type === 'trainer' ? (
                      <Dumbbell className="w-5 h-5 text-primary" />
                    ) : (
                      <User className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      {activeConversation.other_participant_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activeConversation.other_participant_type === 'trainer' ? 'Trainer' : 'Client'}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>No messages yet</p>
                      <p className="text-sm">Send a message to start the conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              msg.sender_id === currentUserId
                                ? 'bg-primary text-white'
                                : 'bg-[#1A1A1A] text-white'
                            }`}
                          >
                            <p className="break-words">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === currentUserId ? 'text-white/70' : 'text-gray-500'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="bg-[#1A1A1A] border-transparent text-white"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-primary text-white"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Select a conversation</p>
                  <p className="text-sm mt-1">Or book a session to start messaging a trainer</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
